import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SpinnerIcon, AlertIcon } from "@/lib/icons";
import { TaskTemplateImportAndValidation } from "@/components/taskTemplate/TaskTemplateImportAndValidation";
import type { ExcelData } from "@/components/taskTemplate/TaskTemplateImportAndValidation";

interface CreateTaskTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  createError: string | null;
  resetCreateError: () => void;
  createTemplateSetWithHierarchy: (
    name: string,
    description: string | null,
    excelData: ExcelData | null
  ) => Promise<{ success: boolean }>;
}

export function CreateTaskTemplateDialog({
  open,
  onOpenChange,
  isSubmitting,
  createError,
  resetCreateError,
  createTemplateSetWithHierarchy,
}: CreateTaskTemplateDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    taskTemplate: null as File | null,
  });
  const [excelData, setExcelData] = useState<ExcelData | null>(null);

  useEffect(() => {
    if (open) {
      setFormData({ name: "", description: "", taskTemplate: null });
      setExcelData(null);
      resetCreateError();
    }
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) resetCreateError();
  };

  const handleSubmit = async () => {
    if (!formData.name) return;
    const result = await createTemplateSetWithHierarchy(
      formData.name,
      formData.description || null,
      excelData
    );
    if (result.success) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[1172px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Luo uusi tehtäväpohja</DialogTitle>
          <DialogDescription>
            Luo uusi tehtäväpohja excel-tiedoston pohjalta. Tiedoston tulee
            olla .xlsx-muodossa ja noudattaa määritettyä rakennetta.
          </DialogDescription>
        </DialogHeader>

        {/* Name */}
        <div className="grid gap-4 py-4 overflow-y-auto min-h-0">
          <div className="grid gap-2">
            <Label htmlFor="create-name">
              Nimi <span className="text-red-500">*</span>
            </Label>
            <Input
              id="create-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="esim. KVR-hankkeen tehtäväpohja"
            />
          </div>

          {/* Description */}
          <div className="grid gap-2">
            <Label htmlFor="create-description">Kuvaus</Label>
            <Textarea
              id="create-description"
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                e.target.style.height = "auto";
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              placeholder="Tehtäväpohjan kuvaus (valinnainen)"
              rows={3}
              className="resize-none overflow-hidden"
            />
          </div>

          {/* File upload */}
          <div className="grid gap-2">
            <Label htmlFor="create-taskTemplate">
              Tehtäväpohja <span className="text-red-500">*</span>
            </Label>
            <TaskTemplateImportAndValidation
              file={formData.taskTemplate}
              onChange={(file) => setFormData({ ...formData, taskTemplate: file })}
              onDataImport={(data) => {
                console.log("Imported task template data:", data);
                setExcelData(data);
              }}
            />
          </div>
        </div>

        {createError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <div className="flex items-start gap-3">
              <AlertIcon className="h-5 w-5 shrink-0 text-red-600 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-red-800">
                  Virhe tehtäväpohjan luonnissa
                </p>
                <p className="text-sm text-red-700">{createError}</p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Peruuta
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.name || isSubmitting}>
            {isSubmitting && <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? "Luodaan..." : "Luo tehtäväpohja"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
